import React from "react"
import { AuthCodeRef } from "react-auth-code-input"
import { FormProvider, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { PROFILE_DELETE_CODE_LENGTH } from "shared/constants/app-constants"
import { ButtonVariant } from "shared/ui/buttons/Button"
import { AuthCodeInput } from "shared/ui/input/auth-code/AuthCodeInput"
import { LoadingSpinner } from "shared/ui/loading-spinner/LoadingSpinner"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import "../styles/_profile-delete-code-overlay.scss"
import { ProfileDeleteCodeVerifyResult } from "./ProfileDeleteCodeVerifyResult"

export interface ProfileDeleteCodeFormData {
  code: string
}

export interface DataTableOverlayViewProps {
  remainingAttempts: number | null
  form: UseFormReturn<ProfileDeleteCodeFormData>
  autoFocus?: boolean
  isCodeCorrect: boolean
  isVerifying: boolean
  isInputDisabled?: boolean
  isSubmitDisabled: boolean

  codeInputRef: React.RefObject<AuthCodeRef | null>

  onClose: () => void
  onSubmit: (ev: React.FormEvent) => void
}

export const ProfileDeleteCodeOverlayView = React.memo(
  ({
    onClose,
    onSubmit,
    codeInputRef,
    remainingAttempts,
    isCodeCorrect,
    isVerifying,
    isSubmitDisabled,
    isInputDisabled,
    form,
    autoFocus = true
  }: DataTableOverlayViewProps) => {
    const { t } = useTranslation()

    return (
      <FormOverlay
        id="profile-delete-code-overlay"
        form="profile-delete-code-overlay-form"
        className="profile-delete-code-overlay"
        headline={t("UserProfile.DeleteProfile.AreYouSure")}
        submitButtonText={t("UserProfile.DeleteProfile.DeleteProfile")}
        submitButtonProps={{
          className: "profile-delete-actions__submit",
          "data-testid": "profile-delete-actions__submit-code",
          isDanger: true
        }}
        isSubmitDisabled={isSubmitDisabled}
        cancelButtonProps={{
          variant: ButtonVariant.SECONDARY,
          className: "profile-delete-actions__cancel",
          label: t("General.Cancel")
        }}
        onCancel={onClose}
        showCloseButton
      >
        <FormProvider {...form}>
          <form
            id="profile-delete-code-overlay-form"
            className="overlay-form"
            onSubmit={onSubmit}
          >
            <div className="profile-delete__info-text">
              {t("UserProfile.DeleteProfile.CodeSubmitOverlay.InfoText")}
            </div>

            <AuthCodeInput.Root
              name="code"
              label={t("UserProfile.DeleteProfile.CodeInputLabel")}
              autoFocus={autoFocus}
            >
              <AuthCodeInput.Input
                ref={codeInputRef}
                disabled={isInputDisabled}
                length={PROFILE_DELETE_CODE_LENGTH}
                containerClassName="profile-delete-code-input__container"
                inputClassName="profile-delete-code-input__input"
                ariaLabel={t("UserProfile.DeleteProfile.CodeInputLabel")}
                allowedCharacters="numeric"
              />

              <AuthCodeInput.InfoMessage>
                {isVerifying ? (
                  <LoadingSpinner
                    small
                    className="code-verify-result__loading"
                  />
                ) : (
                  <ProfileDeleteCodeVerifyResult
                    isCodeCorrect={isCodeCorrect}
                    remainingAttempts={remainingAttempts}
                    validText={t(
                      "UserProfile.DeleteProfile.CodeSubmitOverlay.CorrectCode"
                    )}
                    invalidText={t(
                      "UserProfile.DeleteProfile.CodeSubmitOverlay.IncorrectCode",
                      {
                        remainingAttempts
                      }
                    )}
                  />
                )}
              </AuthCodeInput.InfoMessage>
            </AuthCodeInput.Root>
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
